import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { RootState } from '.'

export interface User {
  email: string | null
  username: string
  code: number
  firstName: string | null
  lastName: string | null
  roles: string[]
}

type Status = 'checking' | 'loggedOut' | 'loggedIn'
type LoginStatus = 'idle' | 'fetching' | 'error' | 'success'

interface AuthState {
  status: Status
  loginStatus: LoginStatus
  errorMessage: string | null
  accessToken: string | null
  pushToken: string | null
  user: User | null
}

export interface Credentials {
  login: string
  password: string
}

interface TokenStart {
  userId: number
  token: string
}

// --- INITAL STATE --- //

const accessToken = localStorage.getItem('accessToken')

export const initialState: AuthState = {
  status: accessToken === null ? 'loggedOut' : 'checking',
  loginStatus: 'idle',
  errorMessage: null,
  accessToken,
  pushToken: null,
  user: null,
}

// --- SLICE --- //

export const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.status = 'loggedOut'
      state.loginStatus = 'idle'
      state.user = null
      state.accessToken = null
      state.pushToken = null
      state.errorMessage = null
      localStorage.removeItem('accessToken')
    },

    loginStart: (state, _: PayloadAction<Credentials>) => {
      state.loginStatus = 'fetching'
    },
    loginFail: (state, { payload }: PayloadAction<string>) => {
      state.loginStatus = 'error'
      state.errorMessage = payload
    },
    loginSuccess: (state, { payload }: PayloadAction<string>) => {
      state.accessToken = payload
      state.errorMessage = null
      // Переводим в 'checking', чтобы отработал тот же путь, что и при загрузке с
      // токеном из localStorage: getMe (skip при status !== 'checking') → userLoadSuccess
      // → 'loggedIn' → редирект из RefreshToken. Иначе после логина статус остаётся
      // 'loggedOut', getMe не стартует и перехода на /market не происходит.
      state.status = 'checking'
      localStorage.setItem('accessToken', payload)
    },

    userLoadSuccess: (state, { payload }: PayloadAction<User>) => {
      state.user = payload
      state.status = 'loggedIn'
      state.loginStatus = 'success'
    },

    saveTokenStart: (state, { payload }: PayloadAction<TokenStart>) => {
      state.pushToken = payload.token
    },
  },
})

// --- SELECTORS --- //

export const selectAuth = (state: RootState) => state.auth
export const selectAccessToken = (state: RootState) => state.auth.accessToken
export const selectUser = (state: RootState) => state.auth.user
